import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import type { FormContextProps } from 'src/components/form/form/types';
import AssetFormFields from 'src/pages/assets/update/forms/AssetFormFields';
import type { AssetFormFieldData } from 'src/pages/assets/update/forms/assetSchema';
import {
  AssetFormSchema,
  defaultValues,
} from 'src/pages/assets/update/forms/assetSchema';
import { getContributors, getOwners } from 'src/rbac/contributorHelper';
import type { FileOrRelation } from 'src/schemas/global';

type Props = Omit<
  FormContextProps<AssetFormFieldData>,
  | 'defaultValues'
  | 'formId'
  | 'i18n'
  | 'parentType'
  | 'renderTemplate'
  | 'schema'
> & { beforeFieldsSlot?: ReactNode; assetId?: string };

const AssetForm: FC<Props> = ({ beforeFieldsSlot, assetId, ...props }) => {
  const { t } = useTranslation('common');

  return (
    <CustomisableForm
      {...props}
      header={t('details')}
      schema={AssetFormSchema}
      defaultValues={defaultValues}
      i18n={t('assets')}
      formId={'asset-form'}
      parentType={Parent_Type_Enum.Asset}
      renderTemplate={(renderProps) => (
        <PageWrapper {...renderProps} testId={'asset-form'} />
      )}
      approvalConfig={{ object: { Id: assetId ?? '' } }}
      mapPreviewedChanges={(
        current: AssetFormFieldData | undefined,
        incomingChanges:
          | (AssetFormFieldData & {
              Owners: { UserId: string }[];
              OwnerGroups: { UserGroupId: string }[];
              Contributors: { UserId: string }[];
              ContributorGroups: { UserGroupId: string }[];
            })
          | null
          | undefined,
        incomingFiles: FileOrRelation[] | undefined
      ): AssetFormFieldData => {
        return {
          ...defaultValues,
          ...current,
          ...incomingChanges,
          Owners: incomingChanges
            ? getOwners({
                owners: incomingChanges.Owners,
                ownerGroups: incomingChanges.OwnerGroups,
              })
            : (current?.Owners ?? []),
          Contributors: incomingChanges
            ? getContributors({
                contributors: incomingChanges.Contributors,
                contributorGroups: incomingChanges.ContributorGroups,
              })
            : (current?.Contributors ?? []),
          files: [...(current?.files ?? []), ...(incomingFiles ?? [])],
        };
      }}
    >
      {beforeFieldsSlot}
      <AssetFormFields readonly={props.readOnly} />
    </CustomisableForm>
  );
};

export default AssetForm;
